import type React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { C } from '../components/ChatAssistant/compact/tokens';
import { font as f } from '../components/ChatAssistant/compact/style';
import { Icon } from '../components/ChatAssistant/compact/kit';
import { Modal, ModalHead } from '../components/Payments/ui';

/** The installer on the update feed (R2 bucket onlymindai-updates). Its name
 *  carries the version, so bump this with each desktop-v* release. */
export const WINDOWS_INSTALLER_URL = 'https://updates.onlymindai.com/Onlymind-Setup-0.1.2.exe';

/** The macOS disk image on the same feed (universal: Apple Silicon + Intel).
 *  Ad-hoc signed, not notarized, so Gatekeeper asks for "Open Anyway". */
export const MAC_INSTALLER_URL = 'https://updates.onlymindai.com/Onlymind-0.1.2-mac.dmg';
/** Clears the download quarantine when Gatekeeper calls the app "damaged". */
const MAC_UNQUARANTINE = 'xattr -cr /Applications/Onlymind.app';

const row = { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: `1px solid ${C.border}`, borderRadius: 10, background: C.input } as const;
const name = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 } as const;
const code = { font: f(500, 11.5), fontFamily: 'var(--font-mono)', color: C.text, background: C.segActive, padding: '1px 5px', borderRadius: 4, display: 'inline-block', whiteSpace: 'nowrap', marginTop: 4 } as const;

function PlatformRow({ label, hint, href }: { label: string; hint: string; href: string }) {
  const { t } = useTranslation();
  return (
    <div style={row}>
      <Icon name="monitor" size={18} color={C.purpleLt} />
      <div style={name}>
        <span style={{ font: f(600, 13.5), color: C.text }}>{label}</span>
        <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>
      </div>
      <a
        href={href}
        download
        className="pay-primary"
        style={{ height: 32, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 7, borderRadius: 7, background: C.purple, color: '#fff', font: f(600, 13), textDecoration: 'none', flex: 'none' }}
      >
        <Icon name="download" size={13} />
        {t('downloadApp.download')}
      </a>
    </div>
  );
}

function Note({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 8, background: C.tint16, border: `1px solid ${C.tintLine}` }}>
      <Icon name="shield" size={15} color={C.purplePale} style={{ flex: 'none', marginTop: 1 }} />
      <span style={{ font: f(400, 12, 1.5), color: C.body }}>
        <b style={{ color: C.text, fontWeight: 600 }}>{title}</b> {children}
      </span>
    </div>
  );
}

/** "Download the app": Windows installer and macOS disk image, each with its
 *  unsigned-app first-run note. Portalled to <body> because the sidebar it
 *  opens from moves on transforms. */
export function DownloadAppDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return createPortal(
    <Modal width={460} onClose={onClose}>
      <ModalHead icon="download" title={t('downloadApp.title')} onClose={onClose} />
      <div style={{ padding: '6px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ font: f(400, 12.5, 1.5), color: C.sub }}>{t('downloadApp.intro')}</span>

        <PlatformRow label={t('downloadApp.windows')} hint={t('downloadApp.windowsHint')} href={WINDOWS_INSTALLER_URL} />
        <PlatformRow label={t('downloadApp.mac')} hint={t('downloadApp.macHint')} href={MAC_INSTALLER_URL} />

        <Note title={t('downloadApp.smartScreenTitle')}>{t('downloadApp.smartScreenBody')}</Note>
        <Note title={t('downloadApp.gatekeeperTitle')}>
          {t('downloadApp.gatekeeperBody')} {t('downloadApp.gatekeeperDamaged')}{' '}
          <code style={code}>{MAC_UNQUARANTINE}</code>
        </Note>
      </div>
    </Modal>,
    document.body,
  );
}
